/* -------------------------------------------
   HeaderMegamenu
-------------------------------------------- */
class HeaderMegamenu extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  normalizeId(id) {
    return id?.replace(/\s+/g, '').trim() || '';
  }

  init() {
    this.querySelectorAll('details').forEach(detail => {
      const menuId = this.normalizeId(detail.dataset.menuId);
      const has2menu = detail.dataset.has_2_menu === "true";

      const menuContents = [...detail.querySelectorAll('.mega-menu__content')];
      const desktopMenus = [...detail.querySelectorAll('.megamenu-desktop')];

      let matched = null;

      const fallbackMenus = menuContents.filter(mc => !mc.dataset.itemId);

      menuContents.forEach(mc => {
        let itemId = this.normalizeId(mc.dataset.itemId);
        const parent = mc.closest('.megamenu-desktop');

        if (!itemId || !menuId) return;

        if (itemId.includes('-') && !menuId.includes('-') && !has2menu) {
          itemId = itemId.split('-').pop();
        }

        const isMatch = itemId === menuId;

        mc.classList.toggle('hidden', !isMatch);
        parent?.classList.toggle('hidden', !isMatch);
        parent?.classList.toggle('matched-menu', isMatch);

        if (isMatch) matched = { mc, parent };
      });

      /* Fallback handling */
      if (fallbackMenus.length) {
        const fallback = fallbackMenus[0];

        if (matched) {
          matched.mc.classList.add('hidden');
          matched.parent?.classList.remove('matched-menu');
          matched.parent?.classList.add('hidden');

          fallback.classList.remove('hidden');
          fallback.classList.add('matched-menu');

          try {
            fallback.style.cssText = matched.mc.style.cssText;
          } catch (_) {}
        } else {
          fallback.classList.remove('hidden');
          fallback.classList.add('matched-menu');
        }

        fallbackMenus.slice(1).forEach(f => f.classList.add('hidden'));
      }

      const allHidden =
        desktopMenus.every(d => d.classList.contains('hidden')) &&
        fallbackMenus.every(f => f.classList.contains('hidden'));

      detail.classList.toggle('all-hide', allHidden);
    });
  }
}

customElements.define('header-megamenu', HeaderMegamenu);


/* -------------------------------------------
   NavMegamenu
-------------------------------------------- */
class NavMegamenu extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.stickyHeaderRoot = document.querySelector('.section-header');
    this.animations = new Map();
    this.hoverTimeouts = new Map();
    this.activeDetail = null;

    /* header-megamenu children upgrade after this element during initial
       parse, so defer until they've had a chance to set .matched-menu. */
    queueMicrotask(() => {
      this.init();
      this.showLinks();
      this.initLogoHover();
    });
  }

  /* ---------- LOGO HOVER ---------- */

  initLogoHover() {
    const logos = [
      document.getElementById('normalHeader'),
      document.getElementById('BigLogoHeader'),
    ].filter(Boolean);

    logos.forEach(logo => {
      logo.addEventListener('mouseenter', () => {
        if (this.activeDetail) {
          clearTimeout(this.hoverTimeouts.get(this.activeDetail));
          this.hoverTimeouts.delete(this.activeDetail);
          document.body.classList.add('megamenu-open');
        }
      });

      logo.addEventListener('mouseleave', e => {
        if (logos.some(l => l !== logo && l.contains(e.relatedTarget))) return;

        const detail = this.activeDetail;
        if (!detail) {
          document.body.classList.remove('megamenu-open');
          this.header?.style.removeProperty('--mega-menu-height');
          return;
        }

        const menu = this.getMenu(detail);
        if (detail.matches(':hover') || menu?.matches(':hover')) return;

        this.closeDelayed(detail);
        document.body.classList.remove('megamenu-open');
      });
    });
  }

  /* ---------- HELPERS ---------- */

  getMenu(detail) {
    return (
      detail.querySelector('.megamenu-desktop.matched-menu') ||
      detail.querySelector('.mega-menu__content.matched-menu')
    );
  }

  shouldBeTransparent() {
    return window.scrollY <= 150;
  }

  setHeaderTransparentIfAllowed() {
    if (
      this.header?.dataset.headerVersion === 'transparent' &&
      this.shouldBeTransparent()
    ) {
      this.header.classList.add('transparent');
    }
  }

  removeHeaderTransparency() {
    if (this.header?.dataset.headerVersion === 'transparent') {
      this.header.classList.remove('transparent');
    }
  }

  /* ---------- SHOW / HIDE LINKS ---------- */

  showLinks() {
    this.querySelectorAll('header-megamenu details').forEach(detail => {
      const li = detail.closest('li');
      const anchor = li?.querySelector('a.menu-item');
      const hasMenu = !!this.getMenu(detail);

      anchor?.classList.toggle('hidden', hasMenu);
      detail.classList.toggle('hidden', !hasMenu);
    });
  }

  /* ---------- INIT ---------- */

  init() {
    const details = this.querySelectorAll('details');

    details.forEach(detail => {
      const summary = detail.querySelector('summary');
      summary?.addEventListener('click', e => {
        if (!e.target.closest('a')) e.preventDefault();
      });

      detail.addEventListener('mouseenter', () => {
        this.open(detail, details);
        document.body.classList.add('megamenu-open');
      });

      detail.addEventListener('mouseleave', e => {
        const menu = this.getMenu(detail);
        if (menu && menu.contains(e.relatedTarget)) return;

        this.closeDelayed(detail);
        document.body.classList.remove('megamenu-open');
      });
    });
  }

  /* ---------- INTERACTIONS ---------- */

  open(detail, all) {
    clearTimeout(this.hoverTimeouts.get(detail));
    this.hoverTimeouts.delete(detail);
    if (
      detail.hasAttribute('open') &&
      this.activeDetail === detail &&
      !this.animations.has(detail)
    ) return;

    this.cancel(detail);

    const carryHeight = parseFloat(this.header?.style.getPropertyValue('--mega-menu-height')) || 0;
    const switchingMenu = this.activeDetail !== null && this.activeDetail !== detail;

    this.activeDetail = detail;

    all.forEach(d => {
      if (d !== detail) {
        clearTimeout(this.hoverTimeouts.get(d));
        this.hoverTimeouts.delete(d);
      }
      if (d !== detail && d.hasAttribute('open')) {
        this.cancel(d);
        this.closeImmediate(d, { keepHeaderHeight: true });
      }
    });

    if (this.stickyHeaderRoot) {
      this.stickyHeaderRoot.preventHide = true;
      this.stickyHeaderRoot.classList.add('menu-open');
    }

    this.fadeIn(detail, switchingMenu ? carryHeight : 0);
  }

  closeDelayed(detail) {
    clearTimeout(this.hoverTimeouts.get(detail));
    const timeout = setTimeout(() => {
      this.hoverTimeouts.delete(detail);
      const menu = this.getMenu(detail);
      if (detail.matches(':hover') || menu?.matches(':hover')) return;
      this.fadeOut(detail);
    }, 150);
    this.hoverTimeouts.set(detail, timeout);
  }

  cancel(detail) {
    const raf = this.animations.get(detail);
    if (raf) {
      cancelAnimationFrame(raf);
      this.animations.delete(detail);
    }
  }

  /* ---------- ANIMATIONS ---------- */

  runAccordion(detail, menu, from, to, duration, onDone) {
    this.cancel(detail);
    const start = performance.now();

    const step = (now) => {
      if (this.animations.get(detail) === undefined) return;

      const progress = Math.min(1, (now - start) / duration);
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2; // ease-in-out-cubic
      const height = from + (to - from) * eased;

      menu.style.height = `${height}px`;
      if (detail === this.activeDetail || (to === 0 && this.activeDetail === null)) {
        this.header?.style.setProperty('--mega-menu-height', `${height}px`);
      }

      if (progress < 1) {
        this.animations.set(detail, requestAnimationFrame(step));
      } else {
        this.animations.delete(detail);
        onDone();
      }
    };

    this.animations.set(detail, requestAnimationFrame(step));
  }

  fadeIn(detail, startHeight = 0) {
    const menu = this.getMenu(detail);
    if (!menu) return;

    this.removeHeaderTransparency();
    detail.setAttribute('open', '');

    const currentHeight = menu.style.visibility === 'visible' ? menu.offsetHeight : startHeight;

    menu.style.visibility = 'visible';
    menu.style.height = 'auto';
    const targetHeight = menu.offsetHeight;
    menu.style.height = `${currentHeight}px`;
    this.header?.style.setProperty('--mega-menu-height', `${currentHeight}px`);

    this.runAccordion(detail, menu, currentHeight, targetHeight, 350, () => {
      menu.style.height = 'auto';
      this.header?.style.setProperty('--mega-menu-height', `${targetHeight}px`);
    });
  }

  fadeOut(detail) {
    const menu = this.getMenu(detail);
    if (!menu) return this.closeImmediate(detail);

    const startHeight = menu.offsetHeight;
    menu.style.height = `${startHeight}px`;

    this.runAccordion(detail, menu, startHeight, 0, 250, () => {
      this.closeImmediate(detail);
    });
  }

  closeImmediate(detail, { keepHeaderHeight = false } = {}) {
    const menu = this.getMenu(detail);

    if (menu) {
      menu.style.visibility = 'hidden';
      menu.style.height = '';
    }

    detail.removeAttribute('open');
    this.setHeaderTransparentIfAllowed();

    if (this.activeDetail === detail) this.activeDetail = null;

    const anyOpen = this.querySelectorAll('details[open]').length > 0;
    if (!anyOpen && !keepHeaderHeight) this.header?.style.removeProperty('--mega-menu-height');

    if (this.stickyHeaderRoot && !anyOpen) {
      this.stickyHeaderRoot.preventHide = false;
      this.stickyHeaderRoot.classList.remove('menu-open');
    }
  }
}

customElements.define('nav-megamenu', NavMegamenu);
